import React, { useMemo } from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton
} from '@material-ui/core'
import { useStyles } from './styles'
import { useSelector, useDispatch } from 'react-redux'
import { MoreVertical } from 'lucide-react'
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts'
import moment from 'moment'
import { useQuery } from 'react-query'
import { getDeviceBaitActivities } from '../../api/device-api'
import { enqueueSnackbar } from 'web/src/app/root/actions'

export const DeviceBaitActivitiesChart = ({ device }) => {
    const classes = useStyles()
    const { theme, locale } = useSelector(state => state.main)
    const dispatch = useDispatch()
    const {
        data: baitActivities,
        isLoading: isLoadingBaitActivities
    } = useQuery(
        ['getDeviceBaitActivities', device.id],
        () => getDeviceBaitActivities(device.id),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: true,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const baitActivitiesData = useMemo(() => {
        if (!baitActivities) return {}
        return baitActivities.reduce((acc, activity) => {
            const dateFormat = 'DD.MM.YYYY'
            const utcTime = moment(activity.created_at).utc()
            const date = moment(utcTime)
                .local()
                .format(dateFormat)
            if (!acc[date]) {
                acc[date] = 0
            }
            acc[date] += 1
            return acc
        }, {})
    }, [baitActivities])

    const chartData = useMemo(() => {
        const data = []
        for (let i = 29; i >= 0; i--) {
            const date = moment().subtract(i, 'days')
            const fullDate = date.format('DD.MM.YYYY')
            data.push({
                date: date.format('D'),
                activities: baitActivitiesData[fullDate] || 0,
                fullDate: fullDate
            })
        }
        return data
    }, [baitActivitiesData])

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <Box className={classes.chartTooltip}>
                    <Typography>{payload[0].payload.fullDate}</Typography>
                    <Typography>
                        {locale.devices.activities}: {payload[0].value}
                    </Typography>
                </Box>
            )
        }
        return null
    }

    return (
        <Card className={classes.activityChartCard}>
            <CardContent>
                <Box className={classes.activityChartHeader}>
                    <Box>
                        <Typography className={classes.activityChartTitle}>
                            {locale.locations.activity ||
                                locale.devices.activitiesSummary}
                        </Typography>
                        <Typography className={classes.activityChartSubtitle}>
                            {locale.devices.trackActivitiesInsights}
                        </Typography>
                    </Box>
                    {/* <IconButton size="small">
                        <MoreVertical size={20} />
                    </IconButton> */}
                </Box>

                <Box className={classes.activityChartWrapper}>
                    <ResponsiveContainer width="100%" height={200}>
                        <LineChart
                            data={chartData}
                            margin={{
                                top: 10,
                                right: 30,
                                left: 0,
                                bottom: 0
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={theme.grayLight}
                            />
                            <XAxis
                                dataKey="date"
                                tick={{
                                    fill: theme.textSecondary,
                                    fontSize: 12
                                }}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{
                                    fill: theme.textSecondary,
                                    fontSize: 12
                                }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="activities"
                                stroke={theme.active}
                                strokeWidth={2}
                                dot={{
                                    fill: theme.active,
                                    strokeWidth: 2,
                                    r: 3
                                }}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    )
}
